import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { LuBell, LuCoins, LuMenu, LuSearch } from 'react-icons/lu';
// import lgLogo from '../../../assets/nav/new-logo-lg-2.svg';
import { AuthContext } from '../../../Context/authContext';
import Notifications from '../../molecules/Notifications';
import useClickOutside from '../../../hooks/useClickOutside';
import ProfileMenu from './ProfileMenu';
import { NAV_ITEMS } from './navItems';
import { prefetchView } from '../../../Pages/prefetch';
import { StyledMobileNav, StyledTopBar } from './TopBar.styles';
import { useTheme } from '../../../theme';

/**
 * Items the workspace permission list never governs: Workforce has no
 * permission row, and the three placeholders do not exist server-side yet.
 * Everything else stays subject to `allowedPages` so the bar cannot offer a
 * destination RouteGuard will bounce.
 */
const UNGOVERNED_PATHS = new Set(['ai-home', 'workforce', 'tasks', 'analytics']);

const formatCreditBalance = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0';
  return String(Math.round(amount * 100) / 100);
};

/** Detail routes that should keep their parent nav item highlighted. */
const NAV_PREFIXES = {
  'campaign-manager': ['/campaign-manager/'],
  'leads-module': ['/lead-preview'],
  'email-accounts': ['/linkedin-accounts', '/connect-email-account'],
};

const TopBar = () => {
  const location = useLocation();
  const { isDark } = useTheme();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const bellRef = useRef(null);
  const mobileNavRef = useRef(null);

  const { allowedPages, user } = useContextHook(AuthContext, v => ({
    allowedPages: v.allowedPages,
    user: v.user,
  }));

  const aiHomeCredits = useMemo(() => {
    const list = user?.data?.userCredits || user?.userCredits || [];
    const row = list.find(item => item?.creditType === 'ai_home');
    const remaining = Number(row?.remaining);
    return Number.isFinite(remaining) ? remaining : null;
  }, [user]);
  /*
   * The balance comes with user-details, a moment after the bar is drawn. Until
   * then the pill keeps its place with a placeholder where the number goes, so
   * nothing pops in or shifts when it arrives. The login response carries no
   * credits, so "loaded" means the full user-details (its pageData) is in.
   */
  const userData = user?.data || user;
  const creditsLoading = !userData?.id || !userData?.pageData;
  const showCredits = creditsLoading || aiHomeCredits != null;

  useClickOutside(
    bellRef,
    useCallback(() => setNotificationsOpen(false), []),
  );
  useClickOutside(
    mobileNavRef,
    useCallback(() => setMobileNavOpen(false), []),
  );

  const navItems = useMemo(() => {
    if (!allowedPages?.length) return NAV_ITEMS;
    return NAV_ITEMS.filter(item => UNGOVERNED_PATHS.has(item.path) || allowedPages.includes(`/${item.path}`));
  }, [allowedPages]);

  const isActive = useCallback(
    path => {
      const { pathname } = location;
      const prefixes = NAV_PREFIXES[path];
      if (prefixes?.some(prefix => pathname.startsWith(prefix))) return true;
      return pathname === `/${path}`;
    },
    [location],
  );

  const navClassName = path => (isActive(path) ? 'nav-item active' : 'nav-item');

  return (
    <StyledTopBar>
      <div className="topbar-inner">
        <Link to="/ai-home" className="brand">
          {/* Served from public/, so PUBLIC_URL rather than an import: the file is
              copied through as-is instead of being hashed into the bundle, which
              means the brand can be swapped by replacing the file. PUBLIC_URL keeps
              it correct if the app is ever served from a sub-path. */}
          <img src={`${process.env.PUBLIC_URL}/${isDark ? 'tigaly-ai-logo-dark.svg' : 'tigaly-ai-logo.svg'}`} alt="Tigaly AI" />
        </Link>

        <nav className="topbar-nav">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={`/${item.path}`}
              className={navClassName(item.path)}
              id={`nav-${item.path}`}
              // Fetch the page's code as the pointer arrives, ahead of the click.
              onMouseEnter={() => prefetchView(item.path)}
              onFocus={() => prefetchView(item.path)}
              onPointerDown={() => prefetchView(item.path)}>
              {item.name}
            </NavLink>
          ))}
        </nav>

        <div className="topbar-actions">
          {showCredits && (
            <div
              className={`nav-credits${creditsLoading ? ' is-loading' : ''}`}
              title="AI Home credits"
              aria-busy={creditsLoading || undefined}
              aria-label={creditsLoading ? 'Loading credits' : `${formatCreditBalance(aiHomeCredits)} credits`}>
              <span className="nav-credits-icon" aria-hidden>
                <LuCoins />
              </span>
              {creditsLoading ? (
                <span className="nav-credits-placeholder" aria-hidden />
              ) : (
                <span className="nav-credits-value">{formatCreditBalance(aiHomeCredits)}</span>
              )}
              <span className="nav-credits-label">credits</span>
            </div>
          )}
          {/* Parked — the ⌘K search.

              There is nothing behind it: the button carries no handler, so every
              click was a dead end in the most prominent spot on the bar. Hidden
              rather than marked "coming soon" — a search box invites typing, and
              a tooltip does not stop someone typing into it. Unwrap to restore.

          <button type="button" className="search-btn">
            <LuSearch size={16} />
            <span>Search</span>
            <kbd>⌘K</kbd>
          </button>
          */}

          {/* Parked — the notifications bell.

              Hidden rather than marked "coming soon": a bell is read as a live
              signal, and a greyed one still looks like it is telling you there is
              nothing new. The panel, its outside-click handling and the socket feed
              behind it are all untouched — unwrap to bring the whole thing back.

          <div className="bell-wrap" ref={bellRef}>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setNotificationsOpen(prev => !prev)}
              aria-label="Notifications"
              aria-expanded={notificationsOpen}>
              <LuBell size={18} />
            </button>
            <div className={`notification-panel${notificationsOpen ? ' visible' : ''}`}>
              <Notifications notification_card={notificationsOpen} setNotifications={setNotificationsOpen} />
            </div>
          </div>
          */}

          <ProfileMenu />

          <button
            type="button"
            className="icon-btn mobile-nav-btn"
            onClick={() => setMobileNavOpen(prev => !prev)}
            aria-label="Open navigation"
            aria-expanded={mobileNavOpen}>
            <LuMenu size={18} />
          </button>
        </div>
      </div>

      {mobileNavOpen && (
        <StyledMobileNav ref={mobileNavRef}>
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={`/${item.path}`}
              className={isActive(item.path) ? 'active' : undefined}
              onPointerDown={() => prefetchView(item.path)}
              onClick={() => setMobileNavOpen(false)}>
              {item.name}
            </NavLink>
          ))}
        </StyledMobileNav>
      )}
    </StyledTopBar>
  );
};

export default TopBar;
